import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSelectedLocation } from "../contexts/SelectedLocationContext";
import { useCart } from "../contexts/CartContext";
import { UrlParams } from "../types/Params";
import ImageZoom from "../components/ImageZoom";
import BubbleSelector from "../components/BubbleSelector";
import { useCatalog } from "../contexts/CatalogContext";
import { CatalogObject } from "square";
import {
  toUniqueList,
  getImagesByColor,
  sortColorsByGalleryOrder,
} from "../extensions/helpers";
import { findOrGetItemsByCategory } from "../api";
import Counter from "../components/Counter";
import ProductDetailsDropdown from "../components/ProductDetailsDropdown";
import ProductReviews from "../components/ProductReviews";
import RelatedProducts from "../components/RelatedProducts";
import WishlistButton from "../components/WishlistButton";
import SizeGuide from "../components/SizeGuide";
import { fitForProductName } from "../data/sizeGuide";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { getVariationSku, getVariationPriceCents } from "../types/OrderItem";
import { trackViewContent } from "../lib/meta-pixel";

type ProductPageProps = {
  imageZoomEnabled?: boolean;
};

const ProductPage = ({ imageZoomEnabled = false }: ProductPageProps) => {
  const location = useLocation();
  const { domain, category, product } = useParams<UrlParams>();
  const { setDomain, setCategory, setProduct } = useSelectedLocation();
  const { setIsCartOpen, addToCart } = useCart();
  const {
    getItemByName,
    getItemsByCategory,
    getImagesByIds,
    addItems,
    addImages,
    getCategoryByName,
    getCategoryById,
    colorOption,
    sizeOption,
    getOptionDataByColor,
    getOptionDataBySize,
  } = useCatalog();

  // Size-guide modal (unisex fit + women's guidance) opened from the size label.
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const sizeGuideCloseRef = useRef<HTMLButtonElement>(null);
  const sizeGuideOpenerRef = useRef<HTMLElement | null>(null);

  // Modal keyboard/focus handling (WCAG 2.1.2 / 2.4.3): on open, move focus into
  // the dialog and close on Escape; on close, return focus to the trigger.
  useEffect(() => {
    if (!showSizeGuide) return;
    sizeGuideOpenerRef.current = document.activeElement as HTMLElement;
    sizeGuideCloseRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowSizeGuide(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      sizeGuideOpenerRef.current?.focus();
    };
  }, [showSizeGuide]);

  useEffect(() => {
    setDomain(domain ?? null);
    setCategory(category ?? null);
    setProduct(product ?? null);
  }, [domain, category, product, setDomain, setCategory, setProduct]);

  // When Category isn't selected, coalesce for non-parented categories (domains)
  const domainObject = useMemo(
    () => getCategoryByName(domain ?? ""),
    [domain, getCategoryByName],
  );
  const categoryObject = useMemo(
    () => getCategoryByName(category ?? domain ?? ""),
    [category, domain, getCategoryByName],
  );

  // Selected Product
  const selectedProduct: CatalogObject = useMemo(
    () => location.state?.selectedProduct ?? getItemByName(product ?? ""),
    [location.state?.selectedProduct, getItemByName, product],
  );

  const [isColorSourceOfTruth, setIsColorSourceOfTruth] =
    useState<boolean>(true);

  // All Product Variation Objects
  const productVariationObjects = useMemo(
    () => selectedProduct?.itemData?.variations,
    [selectedProduct?.itemData?.variations],
  );

  // All Product Item Variation
  const productVariations = useMemo(
    () => productVariationObjects?.flatMap((v) => v?.itemVariationData),
    [productVariationObjects],
  );

  // The item gallery, in the order the merchant arranged it in Square — the
  // ordering authority for the swatches and for each colour's photos. It lives
  // on the ITEM, so it is present on the first render (the image objects are
  // fetched later).
  const galleryImageIds = useMemo(
    () => selectedProduct?.itemData?.imageIds ?? [],
    [selectedProduct?.itemData?.imageIds],
  );

  // Available Product Colors and Sizes
  const productColors = useMemo(() => {
    const vars = productVariations
      ?.flatMap((v) => v?.itemOptionValues)
      ?.filter((iov): iov is NonNullable<typeof iov> => !!iov?.itemOptionId)
      ?.filter((iov) => iov.itemOptionId!.includes(colorOption?.id ?? ""))
      ?.map((iov) => iov.itemOptionValueId);
    // Swatch order (and the default colour) follows the item gallery the
    // merchant arranged in Square, not Square's variation order.
    return sortColorsByGalleryOrder(
      toUniqueList(vars ?? []),
      productVariations as any[],
      galleryImageIds,
    );
  }, [productVariations, colorOption, galleryImageIds]);

  const productSizes = useMemo(() => {
    const vars = productVariations
      ?.flatMap((v) => v?.itemOptionValues)
      ?.filter((iov): iov is NonNullable<typeof iov> => !!iov?.itemOptionId)
      ?.filter((iov) => iov.itemOptionId!.includes(sizeOption?.id ?? ""))
      ?.map((iov) => iov.itemOptionValueId);
    return toUniqueList(vars ?? []);
  }, [productVariations, sizeOption]);

  // Methods to get the variations of the other types
  // Given a Color, give me the Sizes for that Color
  const getSizesByColor = useCallback(
    (itemOptionValueId: string) => {
      const variationsByColor = productVariations?.filter((v) =>
        v?.itemOptionValues
          ?.map((iov) => iov.itemOptionValueId)
          .includes(itemOptionValueId),
      );
      return variationsByColor
        ?.flatMap((v) => v?.itemOptionValues)
        ?.filter((iov): iov is NonNullable<typeof iov> => !!iov?.itemOptionId)
        ?.filter((iov) => iov.itemOptionId!.includes(sizeOption?.id ?? ""))
        ?.map((iov) => iov.itemOptionValueId);
    },
    [productVariations, sizeOption],
  );

  // Given a Size, give me the Colors for that Size
  const getColorsBySize = (itemOptionValueId: string) => {
    const variationsBySize = productVariations?.filter((v) =>
      v?.itemOptionValues
        ?.map((iov) => iov.itemOptionValueId)
        .includes(itemOptionValueId),
    );
    return sortColorsByGalleryOrder(
      toUniqueList(
        variationsBySize
          ?.flatMap((v) => v?.itemOptionValues)
          ?.filter((iov): iov is NonNullable<typeof iov> => !!iov?.itemOptionId)
          ?.filter((iov) => iov.itemOptionId!.includes(colorOption?.id ?? ""))
          ?.map((iov) => iov.itemOptionValueId) ?? [],
      ),
      productVariations as any[],
      galleryImageIds,
    );
  };

  // Given a Color, get the sorted images (delegates to shared helper)
  const getColorImages = (colorId: string) =>
    getImagesByColor(
      productVariations as any[],
      colorId,
      getImagesByIds,
      galleryImageIds,
    );

  // Set Initial Values
  const [selectedColor, setSelectedColor] = useState<string | null>(
    location.state?.color ?? null,
  );
  const [selectedSize, setSelectedSize] = useState<string | null>(
    location.state?.size ?? null,
  );
  // Initial Values useEffects
  useEffect(() => {
    if (!selectedColor) {
      setSelectedColor(productColors?.[0]);
    }
  }, [selectedColor, productColors]);
  useEffect(() => {
    if (!selectedSize) {
      setSelectedSize(getSizesByColor(selectedColor ?? "")?.[0] ?? null);
    }
  }, [selectedSize, selectedColor, getSizesByColor]);

  const selectedVariation = productVariationObjects?.find((pv) => {
    const vColor =
      productColors?.length === 0 ||
      pv.itemVariationData?.itemOptionValues?.find(
        (iov) => iov.itemOptionValueId === selectedColor,
      );
    const vSize =
      productSizes?.length === 0 ||
      pv.itemVariationData?.itemOptionValues?.find(
        (iov) => iov.itemOptionValueId === selectedSize,
      );
    return !!vColor && !!vSize;
  });
  // Images for the selected Color
  const selectedColorImages = getColorImages(selectedColor ?? "");

  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [selectedImagePath, setSelectedImagePath] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!selectedImagePath) {
      setSelectedImagePath(selectedColorImages?.[0]?.imageData?.url ?? null);
    }
  }, [selectedImagePath, selectedColorImages]);

  // Canonical URL built from the item's OWN lowest category, not from the URL
  // it happened to be reached by. More than one path renders the same product
  // (the domain grid `/shop/apparel` has no category segment of its own), and
  // useDocumentMeta otherwise canonicalizes to `window.location.pathname` —
  // so each variant would declare itself canonical and split the ranking
  // signal against the sitemap's entry. Undefined (catalog still loading,
  // uncategorized item) falls back to that old pathname behaviour.
  const canonicalPath = useMemo(() => {
    // Walks the item's OWN category order and takes the first non-top-level
    // entry, rather than using getLowestCategoryByItem (which scans the
    // catalog's order). sitemap.js and product-meta.js both use item order, and
    // all three emit a canonical for the same page — if an item ever carries
    // two child categories, catalog order could pick the other one and the
    // canonicals would disagree. Every item is filed under its domain as well
    // as its child — "Apparel" is listed FIRST on all 15 products — so the
    // isTopLevel test is what makes this pick "Hats" instead of "Apparel".
    const child = (selectedProduct?.itemData?.categories ?? [])
      .map((c) => getCategoryById(c.id ?? ""))
      .find((c) => c && !c.categoryData?.isTopLevel);
    const parent = getCategoryById(
      child?.categoryData?.parentCategory?.id ?? "",
    );
    const names = [
      parent?.categoryData?.name,
      child?.categoryData?.name,
      selectedProduct?.itemData?.name,
    ];
    if (names.some((n) => !n)) return undefined;
    // Encoded exactly like sitemap.js's `segment()` so the two strings match
    // byte for byte — a canonical that differs only in escaping is a new URL.
    const [d, c, p] = names.map((n) => encodeURIComponent(n!.toLowerCase()));
    return `/shop/${d}/category/${c}/product/${p}`;
  }, [selectedProduct, getCategoryById]);

  // Per-product tab title + link-preview tags (crawler-side injection is
  // handled by netlify/edge-functions/product-meta.js).
  useDocumentMeta({
    title: selectedProduct?.itemData?.name ?? undefined,
    description:
      selectedProduct?.itemData?.descriptionPlaintext ??
      selectedProduct?.itemData?.description ??
      undefined,
    image: selectedImagePath ?? undefined,
    path: canonicalPath,
  });

  // Meta pixel ViewContent. Deduped by SKU so toggling back to a color already
  // seen doesn't re-report, while a genuine variant switch — the thing catalog
  // matching keys off — still counts.
  const lastViewedSku = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedProduct || !selectedVariation) return;
    const sku = getVariationSku(selectedVariation);
    if (!sku || lastViewedSku.current === sku) return;
    lastViewedSku.current = sku;
    trackViewContent({
      sku,
      name: selectedProduct.itemData?.name ?? "",
      priceCents: getVariationPriceCents(selectedVariation),
    });
  }, [selectedProduct, selectedVariation]);

  const [quantity, setQuantity] = useState<number>(1);
  const handleAddToCart = () => {
    addToCart(
      {
        quantity: 1,
        product: selectedProduct,
        variation: selectedVariation as CatalogObject,
      },
      quantity,
    );
    setIsCartOpen(true);
  };

  // TODO: in future with enough items, will retrieve single item by name, for now just pulling in entire category's items
  useEffect(() => {
    const fetchItems = async () => {
      try {
        await findOrGetItemsByCategory(
          categoryObject?.id ?? "",
          addItems,
          getItemsByCategory,
          addImages,
          getImagesByIds,
        );
      } catch (error) {
        console.error("Get items failed:", error);
      }
    };

    // Fetch items if category items haven't been fetched
    if (!!categoryObject?.id) {
      fetchItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainObject, categoryObject]);

  useEffect(() => {
    window.scrollTo(0, 0); // Scrolls to the top
  }, []); // Runs only once when the component mounts

  return (
    <>
      <div className="product-page">
        {/* Left: Image Thumbnails (Gallery). A row on mobile, a column beside
            the hero on desktop. Deliberately unframed — the reference layout
            reads as flat editorial plates, so no border/shadow/radius. */}
        <div className="product-page__thumbs">
          {selectedColorImages?.map((image, index) => (
            <button
              key={index}
              type="button"
              className={`product-page__thumb ${
                selectedImagePath === image?.imageData?.url
                  ? "product-page__thumb--active"
                  : ""
              }`}
              onClick={() =>
                setSelectedImagePath(image?.imageData?.url ?? null)
              }
              aria-label={`Show image ${index + 1}`}
              aria-pressed={selectedImagePath === image?.imageData?.url}
            >
              <img
                src={image?.imageData?.url + "?v=1"}
                alt={
                  image?.imageData?.name ??
                  `${selectedProduct?.itemData?.name ?? "Product"} image ${index + 1}`
                }
                loading="lazy"
              />
            </button>
          ))}
        </div>

        {/* Center: Featured Image */}
        <div className="product-page__hero">
          {/* Hidden until image loads */}
          {!imageLoaded && <div className="product-page__hero-skeleton" />}
          <div className={imageLoaded ? "block" : "hidden"}>
            {!!imageZoomEnabled ? (
              <ImageZoom
                imageUrl={selectedImagePath ?? ""}
                imageAlt={`${selectedProduct?.itemData?.name}_center`}
                onLoad={() => setImageLoaded(true)}
              />
            ) : (
              <img
                src={selectedImagePath ?? undefined}
                alt={`${selectedProduct?.itemData?.name}_center`}
                className="product-image"
                onLoad={() => setImageLoaded(true)}
              />
            )}
          </div>
        </div>

        {/* Right: Product Details */}
        <div className="product-page__details">
          <div className="flex items-start justify-between gap-3">
            <h1 className="product-page__title">
              {selectedProduct?.itemData?.name}
            </h1>
            <WishlistButton
              productId={selectedProduct?.id}
              className="p-1.5 mt-2 shrink-0"
            />
          </div>

          <ProductReviews itemId={selectedProduct?.id} />

          <p className="product-page__price">
            $
            {(
              Number(selectedVariation?.itemVariationData?.priceMoney?.amount) /
              100
            ).toFixed(2)}
          </p>

          <div className="product-page__field">
            <label htmlFor="color" className="product-page__label">
              Color
            </label>
            <BubbleSelector
              selected={selectedColor}
              setSelected={(value) => {
                const newColor = value.id;
                // 1. Set Color
                setSelectedColor(newColor);
                setIsColorSourceOfTruth(true); // Always default back on selection
                // 2. After choosing a color - switch selectedImagePath
                setSelectedImagePath(
                  getColorImages(newColor)?.[0]?.imageData?.url ?? null,
                );
                // 3. Set new size if current size isn't in variations of selected color
                const newSizesByColor = getSizesByColor(newColor);
                if (!newSizesByColor?.includes(selectedSize ?? "")) {
                  setSelectedSize(newSizesByColor?.[0] ?? null);
                }
              }}
              options={productColors?.map((c) => ({
                id: c,
                name: getOptionDataByColor(c)?.itemOptionValueData?.name ?? "",
                color:
                  getOptionDataByColor(c)?.itemOptionValueData?.color ??
                  undefined,
              }))}
              availableOptions={
                // If Color is chosen as source of truth, get all Colors for product
                // else, get only Colors available for those Sizes
                isColorSourceOfTruth
                  ? productColors?.map((c) => ({
                      id: c,
                      name:
                        getOptionDataByColor(c)?.itemOptionValueData?.name ??
                        "",
                      color:
                        getOptionDataByColor(c)?.itemOptionValueData?.color ??
                        undefined,
                    }))
                  : getColorsBySize(selectedSize ?? "")?.map((c) => ({
                      id: c,
                      name:
                        getOptionDataByColor(c ?? "")?.itemOptionValueData
                          ?.name ?? "",
                      color:
                        getOptionDataByColor(c ?? "")?.itemOptionValueData
                          ?.color ?? undefined,
                    }))
              }
              disabledOptionExplanation="Out of stock"
              className="product-page__swatches"
              isRounded
            />
          </div>

          {productSizes?.length > 0 && (
            <div className="product-page__field">
              <div className="flex items-baseline justify-between">
                <label htmlFor="size" className="product-page__label">
                  Size
                </label>
                <button
                  type="button"
                  onClick={() => setShowSizeGuide(true)}
                  className="product-page__size-guide-link"
                >
                  Size Guide
                </button>
              </div>
              <BubbleSelector
                selected={selectedSize}
                setSelected={(value) => {
                  const newSize = value.id;
                  // 1. Set Size
                  setSelectedSize(newSize);
                  // 2. Set source of truth and new color if current color isn't in variations of selected size
                  const newColorsBySize = getColorsBySize(newSize);
                  if (!newColorsBySize?.includes(selectedColor ?? "")) {
                    setIsColorSourceOfTruth(false);
                    setSelectedColor(newColorsBySize?.[0] ?? null);
                    setSelectedImagePath(
                      getColorImages(newColorsBySize?.[0] ?? "")?.[0]?.imageData
                        ?.url ?? null,
                    );
                  }
                }}
                options={productSizes?.map((c) => ({
                  id: c,
                  name: getOptionDataBySize(c)?.itemOptionValueData?.name ?? "",
                }))}
                availableOptions={
                  // If Color is chosen as source of truth, get only Sizes by available Colors
                  // else, get all Sizes for the product
                  isColorSourceOfTruth
                    ? getSizesByColor(selectedColor ?? "")?.map((c) => ({
                        id: c,
                        name:
                          getOptionDataBySize(c ?? "")?.itemOptionValueData
                            ?.name ?? "",
                      }))
                    : productSizes?.map((c) => ({
                        id: c,
                        name:
                          getOptionDataBySize(c ?? "")?.itemOptionValueData
                            ?.name ?? "",
                      }))
                }
                disabledOptionExplanation="Out of stock"
                className="product-page__sizes"
                isRounded={false}
              />
            </div>
          )}

          <div className="product-page__field">
            <label htmlFor="quantity" className="product-page__label">
              QTY
            </label>
            <Counter
              quantity={quantity}
              add={() => setQuantity((count) => count + 1)}
              remove={() =>
                setQuantity((count) => (count > 1 ? count - 1 : count))
              }
              // No width cap: .counter-container is an inline-flex pill that
              // sizes to its 36px buttons + value (~106px). w-24 (96px) with
              // the pill's overflow-hidden clipped the right edge of the "+".
              className="self-start"
            />
          </div>

          <button
            type="button"
            className="product-page__add-to-cart"
            onClick={handleAddToCart}
            disabled={
              (productColors?.length > 0 && !selectedColor) ||
              (productSizes?.length > 0 && !selectedSize) ||
              !selectedVariation
            }
          >
            Add to Cart
          </button>

          <ProductDetailsDropdown>
            {selectedProduct?.itemData?.descriptionHtml || ""}
          </ProductDetailsDropdown>
        </div>
      </div>

      {/* Cross-sell: siblings from the same category */}
      <div className="px-4 pb-8">
        <RelatedProducts
          product={selectedProduct}
          category={categoryObject}
          domain={domainObject}
        />
      </div>

      {/* Size-guide modal */}
      {showSizeGuide && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Size guide"
          onClick={() => setShowSizeGuide(false)}
        >
          <div
            className="modal-container max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end">
              <button
                ref={sizeGuideCloseRef}
                type="button"
                onClick={() => setShowSizeGuide(false)}
                className="text-2xl leading-none text-gray-400 hover:text-brown-dark"
                aria-label="Close size guide"
              >
                ×
              </button>
            </div>
            <SizeGuide
              fit={fitForProductName(
                selectedProduct?.itemData?.name ?? product,
              )}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ProductPage;
